import { useNavigate } from 'react-router-dom';

import documentRequestCandidateService from 'store/document-request-candidate/service';
import { DOTMedicalQuestionsPage, DriverDocumentsPage } from 'routes/paths';
import { b64toFile } from 'functions/file/b64toFile';
import styles from './styles.module.scss';
import React, { useEffect, useReducer, useState } from 'react';
import StepDescription from '../StepDescription';
import ManualUploadButton from 'features/Driver/ManualUploadButton';
import VideoStream from 'components/VideoStream';
import ImagePreview, { ImagePreviewStatus } from 'components/ImagePreview';
import {
  DotMedScanStepsEnum,
  initialState,
  nextStepAction,
  previousStepAction,
  reducer,
  setImageStatusAction,
  setImageUloadProgressAction,
} from './reducer';
import { useGetUploadLicenseStatus } from 'features/Driver/LicenseCameraScan/useGetUploadLicenseStatus';
import {
  DocumentProcessStatusFieldsEnum,
  documentProcessErrorsStatus,
  DocumentProcessStatusEnum,
} from 'common/document/enums/document-status.enum';
import { fileTob64 } from 'functions/file/fileTob64';
import { UploadImage } from 'components/UploadImage';
import MessageBanner from 'components/MessageBanner';
import { DocumentTypeEnum } from 'common/document/enums/document-type.enum';
import { useDeviceFeatures } from 'hooks/useDeviceFeatures';
import { useFeatureFlags } from '../../../../contexts/featureFlag/useFeatureFlags';
import {
  FOCUS_ERROR_MESSAGE,
  PREDEFINED_ERROR_MESSAGES,
} from 'components/VideoStream/errorMessages';
import { ESegmentCategory, track } from 'integrations/analytics';
import { DocumentVerificationUpdated } from 'common/document/interfaces/document-verification-updated-message';

export interface DriverUploadDotMedicalPageProps {
  documentRequestId: string;
  documentId: string;
  useManulaUpload?: boolean;
}

const DriverUploadDotMedicalPage = (props: DriverUploadDotMedicalPageProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const navigate = useNavigate();
  const [mediaStreamTrack, setMediaStreamTrack] = useState<MediaStreamTrack>();
  const deviceFeatures = useDeviceFeatures(mediaStreamTrack);
  const { featureFlags } = useFeatureFlags();

  const [getUrlToUploadFile] =
    documentRequestCandidateService.endpoints.getPresignedUrlForUploading.useLazyQuery();

  const [checkPreProcessingStatus] =
    documentRequestCandidateService.endpoints.checkPreProcessingStatus.useLazyQuery();

  const uploadDotMedApi = useGetUploadLicenseStatus();

  const onProcessing = () => {
    dispatch(setImageStatusAction(ImagePreviewStatus.PROCESSING));
  };

  const onProgressing = (progress: number) => {
    dispatch(setImageUloadProgressAction(progress));
  };

  const onProcessed = (documentStatus: DocumentVerificationUpdated) => {
    if (documentStatus?.verificationStatus) {
      const status = documentProcessErrorsStatus.includes(
        documentStatus.verificationStatus
      )
        ? ImagePreviewStatus.ERROR
        : ImagePreviewStatus.SUCCESS;
      const defaultMessage = (PREDEFINED_ERROR_MESSAGES as any)[
        documentStatus.verificationStatus
      ];

      if (
        documentStatus?.verificationStatus === DocumentProcessStatusEnum.FAILED
      ) {
        checkPreProcessingStatus({ documentId: props.documentId })
          .unwrap()
          .then((preProcessingStatuses: any) => {
            const preProcessingStatusMap = new Map<string, any>(
              Object.entries(preProcessingStatuses)
            );
            if (
              state.step === DotMedScanStepsEnum.dotMedPreview &&
              preProcessingStatusMap.has(
                DocumentProcessStatusFieldsEnum.DOT_MEDICAL_PRE_PROCESSING_STATUS
              )
            ) {
              const documentStatus = preProcessingStatusMap.get(
                DocumentProcessStatusFieldsEnum.DOT_MEDICAL_PRE_PROCESSING_STATUS
              );
              const defaultMessage = (PREDEFINED_ERROR_MESSAGES as any)[
                documentStatus.verificationStatus
              ];
              dispatch(
                setImageStatusAction(
                  ImagePreviewStatus.SUCCESS,
                  defaultMessage?.message,
                  defaultMessage?.label
                )
              );
            } else {
              dispatch(
                setImageStatusAction(
                  status,
                  defaultMessage?.message,
                  defaultMessage?.label
                )
              );
            }
          })
          .catch(() => {
            dispatch(
              setImageStatusAction(
                status,
                defaultMessage?.message,
                defaultMessage?.label
              )
            );
          });
      } else {
        dispatch(
          setImageStatusAction(
            status,
            defaultMessage?.message,
            defaultMessage?.label
          )
        );
      }
    }
  };

  useEffect(() => {
    (async () => {
      if (state.step === DotMedScanStepsEnum.dotMedPreview) {
        if (
          featureFlags['VALIDATE_BLUR_SCORE'] &&
          state.focusQuality &&
          (state.focusQuality < state.focusQualityThreshold! ||
            state.isBlankImage ||
            state.isWhiteImage)
        ) {
          dispatch(
            setImageStatusAction(
              ImagePreviewStatus.ERROR,
              FOCUS_ERROR_MESSAGE.message,
              FOCUS_ERROR_MESSAGE.label
            )
          );
          track(ESegmentCategory.IMAGE_OUT_OF_FOCUS, {
            type: DocumentTypeEnum.DOT_MEDICAL,
            manualUpload: props.useManulaUpload,
            deviceFeatures,
            validateBlurScore: featureFlags['VALIDATE_BLUR_SCORE'],
            focusQuality: state.focusQuality,
            focusQualityThreshold: state.focusQualityThreshold,
          });
          return;
        }

        let url: any = {};
        try {
          url = await getUrlToUploadFile({
            documentId: props.documentId,
            fileTypes: state?.file?.type ? [state?.file?.type] : ['image/png'],
          }).unwrap();
        } catch (e: any) {
          dispatch(
            setImageStatusAction(ImagePreviewStatus.ERROR, e.message, e.message)
          );
          console.error(e);
        }

        if (url?.certificate) {
          const file =
            state.file ||
            b64toFile(state.dotMedImage!, 'image/png', 'LicenseFront');
          uploadDotMedApi.uploadDocument(
            file,
            url.certificate,
            DocumentProcessStatusFieldsEnum.DOT_MEDICAL_PRE_PROCESSING_STATUS,
            onProgressing,
            onProcessing,
            onProcessed
          );
          track(ESegmentCategory.IMAGE_UPLOADED, {
            type: DocumentTypeEnum.DOT_MEDICAL,
            manualUpload: props.useManulaUpload,
            deviceFeatures,
            validateBlurScore: featureFlags['VALIDATE_BLUR_SCORE'],
            focusQuality: state.focusQuality,
            focusQualityThreshold: state.focusQualityThreshold,
          });
        }
      }

      if (state.step === DotMedScanStepsEnum.dotMedQuestions) {
        navigate(
          `${DOTMedicalQuestionsPage.replaceAll(
            ':documentRequestId',
            props.documentRequestId
          ).replaceAll(':documentId', props.documentId)}?from=${
            DocumentTypeEnum.DOT_MEDICAL
          }`
        );
      }

      if (state.step === DotMedScanStepsEnum.endOfTheFlow) {
        navigate(`${DriverDocumentsPage}?from=${DocumentTypeEnum.DOT_MEDICAL}`);
      }
    })();
  }, [getUrlToUploadFile, state.step]);

  const onImageCapture = (
    image: string,
    focusQuality: number,
    focusQualityThreshold: number,
    isBlankImage?: boolean,
    isWhiteImage?: boolean
  ) => {
    dispatch(
      nextStepAction({
        mainImage: image,
        focusQuality: focusQuality!,
        focusQualityThreshold,
        isBlankImage,
        isWhiteImage,
      })
    );
    track(ESegmentCategory.CAMERA_UPLOAD_IMAGE_SELECTED);
  };

  const onContinueClick = () => {
    dispatch(nextStepAction());
  };

  const onRetakeClick = () => {
    track(ESegmentCategory.RETAKE_PHOTO);
    dispatch(previousStepAction());
  };

  const handleFrontFileUploaded = async (
    file?: File | Blob,
    previewImage?: string
  ) => {
    const base64Image = await fileTob64(file as File, true);
    dispatch(
      nextStepAction(
        {
          mainImage: base64Image,
          focusQuality: 100,
          focusQualityThreshold: -1,
          file: file as File,
        },
        previewImage
      )
    );
    track(ESegmentCategory.MANUAL_UPLOAD_IMAGE_SELECTED);
  };

  const onMediaTrackChanged = (mediaStreamTrack?: MediaStreamTrack) => {
    setMediaStreamTrack(mediaStreamTrack);
  };

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        {(state.step === DotMedScanStepsEnum.dotMed ||
          state.step === DotMedScanStepsEnum.dotMedPreview) && (
          <StepDescription
            title={
              props.useManulaUpload
                ? 'Upload Your Medical Examiner’s Certificate'
                : 'Scan Your Medical Examiner’s Certificate'
            }
            description={
              props.useManulaUpload
                ? 'Please upload a photo of your Medical Examiner’s Certificate (MEC).'
                : 'Position your Medical Examiner’s Certificate (MEC) in the frame and take a photo.'
            }
          />
        )}

        {state.step === DotMedScanStepsEnum.dotMed &&
          !props.useManulaUpload && (
            <VideoStream
              withTarget
              aspectRatio="document"
              onImageCapture={onImageCapture}
              onMediaTrackChanged={onMediaTrackChanged}
              useMultiplePhotos={featureFlags['USE_MULTIPLE_PHOTOS']}
              calculateBlurScore={featureFlags['VALIDATE_BLUR_SCORE']}
            />
          )}
        {state.step === DotMedScanStepsEnum.dotMed && props.useManulaUpload && (
          <>
            <UploadImage
              noEditStep
              key={'dot-med'}
              uploadedFileUrl={''}
              onChange={handleFrontFileUploaded}
              placeholder={''}
            />
            <MessageBanner className={styles.manualUpladMessage}>
              Make sure photo is on a flat well lit surface and all edges are
              visible.
            </MessageBanner>
          </>
        )}

        {state.step === DotMedScanStepsEnum.dotMed &&
          !props.useManulaUpload && (
            <ManualUploadButton
              showMessageBeforeTimeout
              className={styles.manualUploadButton}
              key={state.step}
              documentRequestId={props.documentRequestId}
              documentId={props.documentId}
              documentType="dot-med"
              documentTypeEnum={DocumentTypeEnum.DOT_MEDICAL}
              withTimeout
            />
          )}

        {state.step === DotMedScanStepsEnum.dotMedPreview && (
          <ImagePreview
            file={state.file}
            documentTypeEnum={DocumentTypeEnum.DOT_MEDICAL}
            hideManualUploadButton={props.useManulaUpload}
            documentType="dot-med"
            documentRequestId={props.documentRequestId}
            documentId={props.documentId}
            source={state.imagePreview || state.dotMedImage}
            status={state.imageStatus}
            imageUploadProgress={state.imageUploadProgress}
            errorMessage={state.imageErrorMessage}
            errorLabel={state.imageErrorLabel}
            buttons={{
              hideRetake: state.imageStatus !== ImagePreviewStatus.SUCCESS,
              onApproveClick:
                state.imageStatus === ImagePreviewStatus.SUCCESS
                  ? onContinueClick
                  : onRetakeClick,
              onRetakeClick: onRetakeClick,
              approveText:
                state.imageStatus === ImagePreviewStatus.SUCCESS
                  ? 'Next'
                  : props.useManulaUpload
                  ? 'Select image'
                  : 'Retake Photo',
            }}
          />
        )}
      </div>
    </div>
  );
};

export default DriverUploadDotMedicalPage;
